import { useMemo, useState } from "react";
import { useShallow } from "zustand/shallow";

import { AvatarPicker } from "@/shared/components/common/AvatarPicker";
import { Card, CardBody, Listbox, ListboxItem, Input, Textarea, Button } from "@heroui/react";
import { hooks } from "@/services/api/hooks";
import { useBoundStore } from "@/app/store";
import { resizeAvatarImage } from "@/shared/utils/resizeImage";
import { DrawerAndModal } from "@/shared/components/common/DrawerAndModal";
import { useOpenState } from "@/shared/hooks/useOpenState";
import { usePageLayout } from "@/app/hooks/usePageLayout";

const MAX_BIO_LENGTH = 300;
const MIN_NAME_LENGTH = 3;
const MAX_NAME_LENGTH = 30;

export function SettingsProfileRoute() {
  const [avatar, setAvatar] = useState<Blob | null>(null);
  const [editingField, setEditingField] = useState<"name" | "bio" | null>(null);
  const [editValue, setEditValue] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string>("");
  const userId = useBoundStore(useShallow((state) => state.user.id));
  const deviceType = useBoundStore(useShallow((state) => state.device.type));
  const uploadProfileAvatar = hooks.storage.useUploadProfileAvatar();
  const deleteProfileAvatar = hooks.storage.useDeleteProfileAvatar();
  const updateProfile = hooks.profiles.useUpdateProfile();
  const { data: profile } = hooks.profiles.useFetchProfile(userId);
  const { data: userImages } = hooks.storage.useGetUserProfileImages(userId);
  // Get the compressed image ID (if exists) to display
  const compressedImageId = userImages?.[0]?.id;
  const { data: profileAvatarUrl } = hooks.storage.useGetProfileAvatarUrl(compressedImageId);
  const { isOpen, handleOpenChange } = useOpenState({ id: "profile-edit-drawer" });

  const handleChangeAvatar = async (image: Blob | null) => {
    if (!image || !userId) {
      return;
    }

    const { originalImg, compressedImg } = await resizeAvatarImage(image);

    try {
      // Delete existing profile avatar if it exists
      if (userImages && userImages.length > 0) {
        // Delete the compressed image record (which will cascade to delete original)
        await deleteProfileAvatar.mutateAsync({ userId, imageId: userImages[0].id });
      }

      // Upload new avatar images
      await uploadProfileAvatar.mutateAsync({ userId, originalImg, compressedImg });
      setAvatar(image);
    } catch (error) {
      console.error("Error uploading avatar:", error);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!userId || !userImages || userImages.length === 0) {
      return;
    }

    try {
      // Delete all profile images for this user
      for (const image of userImages) {
        await deleteProfileAvatar.mutateAsync({ userId, imageId: image.id });
      }
      setAvatar(null);
    } catch (error) {
      console.error("Error deleting avatar:", error);
    }
  };

  const handleOpenEdit = (field: "name" | "bio") => {
    setEditingField(field);
    setEditValue(field === "name" ? profile?.name || "" : profile?.bio || "");
    setNameError("");
    handleOpenChange(true);
  };

  const validateName = (name: string): string => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      return "Name is required";
    }
    if (trimmedName.length < MIN_NAME_LENGTH) {
      return `Name must be at least ${MIN_NAME_LENGTH} characters`;
    }
    return "";
  };

  const handleSaveEdit = async () => {
    if (!userId || !editingField) return;

    // Validate name if editing name field
    if (editingField === "name") {
      const error = validateName(editValue);
      if (error) {
        setNameError(error);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      updateProfile.mutateAsync({ userId, profile: { [editingField]: editValue } });
      handleOpenChange(false);
    } catch (error) {
      console.error(`Error updating ${editingField}:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveEdit();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      handleOpenChange(false);
    }
  };

  const pageLayoutConfig = useMemo(() => ({ navbar: { title: "Profile" } }), []);
  usePageLayout(pageLayoutConfig);

  return (
    <>
      <Card className="mx-auto max-w-lg">
        <CardBody>
          <div className="flex w-full flex-col gap-4">
            <div className="my-4 flex justify-center">
              <AvatarPicker
                onDeleteImage={handleDeleteAvatar}
                onSelectImage={handleChangeAvatar}
                img={avatar ? URL.createObjectURL(avatar) : profileAvatarUrl || undefined}
                id="avatar-picker"
              />
            </div>
            <Listbox aria-label="Profile info" onAction={(key) => handleOpenEdit(key as "name" | "bio")}>
              <ListboxItem key="name" description={profile?.name || "Set your display name"}>
                Name
              </ListboxItem>
              <ListboxItem key="bio" description={profile?.bio || "Write a short bio about yourself"}>
                Bio
              </ListboxItem>
            </Listbox>
          </div>
        </CardBody>
      </Card>

      <DrawerAndModal
        id="profile-edit-drawer"
        title={editingField === "name" ? "Edit Name" : "Edit Bio"}
        closeLabel="Cancel"
        confirmLabel="Save"
        onConfirm={handleSaveEdit}
        actionsDisabled={isSubmitting}
        externalState={{
          isOpen,
          onOpenChange: handleOpenChange,
        }}
      >
        {editingField === "name" ? (
          <Input
            autoFocus
            label="Name"
            placeholder="Enter your name"
            value={editValue}
            onValueChange={(value) => {
              setEditValue(value);
              if (nameError) setNameError("");
            }}
            maxLength={MAX_NAME_LENGTH}
            isInvalid={!!nameError}
            errorMessage={nameError}
            description={`${editValue.length}/${MAX_NAME_LENGTH} characters`}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <Textarea
            autoFocus
            description={`${editValue.length}/${MAX_BIO_LENGTH} characters`}
            label="Bio"
            maxLength={MAX_BIO_LENGTH}
            placeholder="Write a short bio about yourself"
            rows={3}
            value={editValue}
            onValueChange={setEditValue}
            onKeyDown={handleKeyDown}
          />
        )}
        {deviceType === "mobile" && (
          <div className="flex gap-2 pt-2">
            <Button variant="flat" fullWidth onPress={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button color="primary" fullWidth onPress={handleSaveEdit}>
              Save
            </Button>
          </div>
        )}
      </DrawerAndModal>
    </>
  );
}
